import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, FileText, Calendar, Trash2, Star, TrendingUp, Users } from 'lucide-react';
import { noteSpaceService, NoteSpace } from '@/services/supabase';
import { useToast } from '@/components/ui/use-toast';
import { HighlightsPanel } from '@/components/HighlightsPanel';
import { highlightsService, Highlight } from '@/services/highlights';
import { InterviewSelectionModal } from '@/components/InterviewSelectionModal';
import { analysisSessionService } from '@/services/analysis';
import { FeatureFlagPanel } from '@/components/FeatureFlagPanel';
import { CookieBanner } from '@/components/CookieBanner';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [sessionName, setSessionName] = useState('Dashboard');
  const [noteSpaces, setNoteSpaces] = useState<NoteSpace[]>([]);
  const [analysisSessions, setAnalysisSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isHighlightsPanelOpen, setIsHighlightsPanelOpen] = useState(false);
  const [recentHighlights, setRecentHighlights] = useState<Highlight[]>([]);
  const [isInterviewSelectionOpen, setIsInterviewSelectionOpen] = useState(false);

  console.log('Dashboard render - user:', !!user, 'authLoading:', authLoading);

  // Load data when user changes
  useEffect(() => {
    // Load note spaces from Supabase
    const loadNoteSpaces = async () => {
      if (!user) {
        setNoteSpaces([]);
        setLoading(false);
        return;
      }

      try {
        console.log('Loading note spaces from Supabase for user:', user?.id);
        const spaces = await noteSpaceService.getAll();
        console.log('Loaded spaces:', spaces, 'count:', spaces.length);
        setNoteSpaces(spaces);

        // Load recent highlights
        const highlights = await highlightsService.getAllHighlights();
        setRecentHighlights(highlights.slice(0, 5)); // Show only 5 most recent

        // Load analysis sessions
        const sessions = await analysisSessionService.getAll();
        setAnalysisSessions(sessions);
      } catch (error) {
        console.error('Error loading note spaces:', error);
        toast({
          title: "Error loading spaces",
          description: "Failed to load your note spaces. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    if (!authLoading) {
      loadNoteSpaces();
    }
  }, [user, authLoading, toast]);

  const createNewNoteSpace = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create a note space.",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }

    try {
      console.log('Creating new note space');
      const newSpace = await noteSpaceService.create('Untitled Interview');
      console.log('Created space:', newSpace);

      setNoteSpaces(prev => [newSpace, ...prev]);

      toast({
        title: "Note space created",
        description: "Your new note space has been created successfully.",
      });

      navigate(`/space/${newSpace.id}`);
    } catch (error) {
      console.error('Error creating note space:', error);
      toast({
        title: "Error creating space",
        description: "Failed to create a new note space. Please try again.",
        variant: "destructive",
      });
    }
  };

  const createMultiAnalysis = async (selectedInterviews: any[], analysisName: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create an analysis.",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }

    try {
      console.log('Creating multi-interview analysis');
      const spaceIds = selectedInterviews.map(interview => interview.id);
      const analysisSession = await analysisSessionService.create(analysisName, spaceIds);
      
      toast({
        title: "Analysis created",
        description: "Your multi-interview analysis has been created successfully.",
      });

      navigate(`/analysis/${analysisSession.id}`);
    } catch (error) {
      console.error('Error creating analysis:', error);
      toast({
        title: "Error creating analysis",
        description: "Failed to create the analysis. Please try again.",
        variant: "destructive",
      });
    }
  };

  const openNoteSpace = (spaceId: string) => {
    navigate(`/space/${spaceId}`);
  };

  const deleteNoteSpace = async (spaceId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await noteSpaceService.delete(spaceId);
      setNoteSpaces(prev => prev.filter(space => space.id !== spaceId));

      toast({
        title: "Note space deleted",
        description: "The note space has been deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting note space:', error);
      toast({
        title: "Error deleting space",
        description: "Failed to delete the note space. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show auth prompt if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          sessionName={sessionName}
          onSessionNameChange={setSessionName}
        />

        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🦬</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome to YakStak</h3>
            <p className="text-gray-600 mb-6">Please sign in to access your note spaces and start organizing your research.</p>
            <Button
              onClick={() => navigate('/auth')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        sessionName={sessionName}
        onSessionNameChange={setSessionName}
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to YakStak</h1>
              <p className="text-gray-600">Organize your thoughts, insights, and research in visual note spaces.</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setIsHighlightsPanelOpen(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Star className="w-4 h-4" />
                View Highlights
              </Button>
              <Button
                onClick={() => setIsInterviewSelectionOpen(true)}
                variant="outline"
                className="flex items-center gap-2"
                disabled={noteSpaces.length < 2}
              >
                <Users className="w-4 h-4" />
                Analyze Across Interviews
              </Button>
              <Button
                onClick={createNewNoteSpace}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Note Space
              </Button>
            </div>
          </div>

          {/* Recent Highlights Section */}
          {recentHighlights.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                  Recent Highlights
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsHighlightsPanelOpen(true)}
                  className="text-amber-600 hover:text-amber-700"
                >
                  View All
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {recentHighlights.map((highlight) => (
                  <Card
                    key={highlight.id}
                    className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/space/${highlight.noteSpaceId}`)}
                  >
                    <div className="flex items-start gap-3">
                      <Star className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 mb-1 truncate">
                          {highlight.noteSpaceName}
                        </p>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                          {highlight.content}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(highlight.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your note spaces...</p>
          </div>
        ) : (
          <>
            {/* Analysis Sessions Section */}
            {analysisSessions.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-amber-500" />
                    Multi-Interview Analyses
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {analysisSessions.map((session) => (
                    <Card
                      key={session.id}
                      className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/analysis/${session.id}`)}
                    >
                      <div className="flex items-start gap-3">
                        <Users className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 mb-1 truncate">
                            {session.name}
                          </p>
                          {session.description && (
                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                              {session.description}
                            </p>
                          )}
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-400">
                              {session.note_space_ids.length} interviews
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(session.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Create New Space Card */}
              <Card
                className="p-6 border-2 border-dashed border-gray-300 hover:border-amber-400 transition-colors cursor-pointer group"
                onClick={createNewNoteSpace}
              >
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Plus className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Create New Space</h3>
                  <p className="text-sm text-gray-500">Start organizing your thoughts</p>
                </div>
              </Card>

              {/* Existing Note Spaces */}
              {noteSpaces.map((space) => (
                <Card
                  key={space.id}
                  className="p-6 hover:shadow-lg transition-shadow cursor-pointer group relative"
                  onClick={() => openNoteSpace(space.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => deleteNoteSpace(space.id, e)}
                      className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate">
                    {space.name}
                  </h3>

                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(space.created_at).toLocaleDateString()}
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {!loading && noteSpaces.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <img className='w-16 h-16' src='/yak-stak-logo.png' alt='YakStak Logo' />
              <div className="text-2xl font-bold text-gray-900 self-center">
                YakStak
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to start stacking?</h3>
            <p className="text-gray-600 mb-6">Create your first note space to begin organizing your research and insights.</p>
            <Button
              onClick={createNewNoteSpace}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Space
            </Button>
          </div>
        )}
      </div>

      <HighlightsPanel
        isOpen={isHighlightsPanelOpen}
        onClose={() => setIsHighlightsPanelOpen(false)}
      />

      <InterviewSelectionModal
        isOpen={isInterviewSelectionOpen}
        onClose={() => setIsInterviewSelectionOpen(false)}
        onConfirm={createMultiAnalysis}
      />
      
      <FeatureFlagPanel />
      <CookieBanner />
      
      {/* Footer with legal links */}
      <footer className="bg-muted/50 border-t border-border mt-8">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <a href="/cookie-policy" className="text-muted-foreground hover:text-primary transition-colors">
              Cookie Policy
            </a>
            <a href="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="text-muted-foreground hover:text-primary transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
